import { Button, styled, Typography } from '@mui/material'
import {
  CheckCircleIcon,
  StudentIllustrationIcon,
  TeacherIllustrationIcon,
} from '@weblite/icons'
import { t } from '$i18n'
import { UserRole } from '$logic/profile/types'

const SelectedBadge = styled(CheckCircleIcon)`
  color: ${({ theme }) => theme.palette.primary500};
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 12px;
  opacity: 0;
  transform: translate(50%, -50%) scale(0.7);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  &[data-show='true'] {
    opacity: 1;
    transform: translate(50%, -50%) scale(1);
  }
`

const StyledButton = styled(Button)`
  &.Mui-disabled {
    background-color: #080f1112;
  }
`

function RoleItem({
  icon,
  selected,
  text,
  description,
  onClick,
  disabled,
}: {
  icon: JSX.Element
  text: string
  description: string
  selected?: boolean
  onClick: () => unknown
  disabled?: boolean
}) {
  return (
    <StyledButton
      disabled={disabled}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '148px',
        border: '1px solid',
        borderColor: selected ? 'primary300' : 'neutral200',
        backgroundColor: selected ? 'primary50' : 'neutral000',
        '&:hover, &:active': {
          backgroundColor: selected ? 'primary50' : 'neutral000',
        },
        borderRadius: '12px',
        gap: '4px',
        padding: '20px 0',
        position: 'relative',
      }}
      onClick={onClick}
    >
      {icon}
      <Typography variant="subtitle3" marginTop="16px" color="neutral900">
        {text}
      </Typography>
      <Typography variant="caption1" color="neutral500">
        {description}
      </Typography>

      <SelectedBadge data-show={selected} />
    </StyledButton>
  )
}

const Root = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`
interface Props {
  value: UserRole | null
  onChange: (value: UserRole) => void
}

export function SelectRole({ value, onChange }: Props) {
  const handleChange = (val: UserRole) => {
    onChange(val)
  }

  return (
    <Root>
      <Typography color="neutral600" variant="subtitle3" display="flex">
        {t('selectRole.title')}
        <Typography color="error" variant="subtitle3" sx={{ margin: '0 4px' }}>
          *
        </Typography>
      </Typography>

      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        <RoleItem
          icon={
            <StudentIllustrationIcon sx={{ width: '25px', height: '48px' }} />
          }
          text={t('selectRole.student')}
          description={t('selectRole.studentDescription')}
          onClick={() => handleChange(UserRole.STUDENT)}
          selected={value === UserRole.STUDENT}
        />
        <RoleItem
          icon={
            <TeacherIllustrationIcon sx={{ width: '48px', height: '48px' }} />
          }
          text={t('selectRole.teacher')}
          description={t('selectRole.teacherDescription')}
          onClick={() => handleChange(UserRole.TEACHER)}
          selected={value === UserRole.TEACHER}
        />
      </div>
    </Root>
  )
}
